// Client metrics per app version, from Axiom: device heartbeats
// (desktopclientheartbeat) and client events (client_events). Used by the
// Android and router version tables.

export interface VersionMetrics {
  version: string
  installCount: number
  activeUsers: number
  heartbeatCount: number
  launchCount: number
  usageEventCount: number
  trafficBytes: number
  trafficUpBytes: number
  trafficDownBytes: number
  eventDeviceCount: number
  eventUserCount: number
  lastHeartbeatAt: number
  lastEventAt: number
  lastSeenAt: number
}

// platform: the heartbeat's platform field. appPrefix: the x-panda-app
// header before the version, as the event report stores it in _clientVersion.
export function versionMetricsApl({ platform, appPrefix, limit }: { platform: string, appPrefix: string, limit: number }) {
  const heartbeatApl = `['desktopclientheartbeat']
        | where platform == '${platform}'
        | where isnotempty(version) and isnotempty(device_uuid)
        | extend version = tostring(version)
        | summarize install_count = dcount(device_uuid), active_users = dcount(userId), heartbeat_count = count(), last_heartbeat = max(_time) by version
        | order by install_count desc
        | limit ${limit}`

  const eventApl = `['client_events']
        | where _clientVersion contains '${appPrefix}'
        | extend version = replace_regex(@'^${appPrefix}', '', tostring(_clientVersion))
        | where isnotempty(version)
        | extend usage_up = todouble(['metadata.usage_up_delta']), usage_down = todouble(['metadata.usage_down_delta'])
        | summarize launch_count = sum(iff(name == 'Service Started', 1, 0)), usage_event_count = sum(iff(name == 'Service Data Usage', 1, 0)), usage_up_bytes = sum(usage_up), usage_down_bytes = sum(usage_down), event_device_count = dcount(_deviceUuid), event_user_count = dcount(_userId), last_event = max(_time) by version
        | extend traffic_bytes = usage_up_bytes + usage_down_bytes
        | order by traffic_bytes desc
        | limit ${limit}`

  return { heartbeatApl, eventApl }
}

// One row per version from the rows of the two queries above.
export function mergeVersionMetrics(heartbeatRows: any[], eventRows: any[], toEpochMs: (value: any) => number): VersionMetrics[] {
  const rowsByVersion = new Map<string, VersionMetrics>()
  const rowFor = (version: any) => {
    const key = String(version || '').trim()
    if (!key) return null
    const existing = rowsByVersion.get(key)
    if (existing) return existing
    const created: VersionMetrics = {
      version: key,
      installCount: 0,
      activeUsers: 0,
      heartbeatCount: 0,
      launchCount: 0,
      usageEventCount: 0,
      trafficBytes: 0,
      trafficUpBytes: 0,
      trafficDownBytes: 0,
      eventDeviceCount: 0,
      eventUserCount: 0,
      lastHeartbeatAt: 0,
      lastEventAt: 0,
      lastSeenAt: 0,
    }
    rowsByVersion.set(key, created)
    return created
  }

  for (const row of heartbeatRows || []) {
    const target = rowFor(row.version)
    if (!target) continue
    target.installCount = Number(row.install_count || 0)
    target.activeUsers = Number(row.active_users || 0)
    target.heartbeatCount = Number(row.heartbeat_count || 0)
    target.lastHeartbeatAt = toEpochMs(row.last_heartbeat)
  }

  for (const row of eventRows || []) {
    const target = rowFor(row.version)
    if (!target) continue
    target.launchCount = Number(row.launch_count || 0)
    target.usageEventCount = Number(row.usage_event_count || 0)
    target.trafficUpBytes = Number(row.usage_up_bytes || 0)
    target.trafficDownBytes = Number(row.usage_down_bytes || 0)
    target.trafficBytes = Number(row.traffic_bytes || target.trafficUpBytes + target.trafficDownBytes)
    target.eventDeviceCount = Number(row.event_device_count || 0)
    target.eventUserCount = Number(row.event_user_count || 0)
    target.lastEventAt = toEpochMs(row.last_event)
  }

  return [...rowsByVersion.values()]
    .map((row) => ({
      ...row,
      lastSeenAt: Math.max(row.lastHeartbeatAt, row.lastEventAt),
    }))
    .sort((a, b) => b.installCount - a.installCount || b.trafficBytes - a.trafficBytes)
}

// Routers report "<name>+<code>" (RouterPanda api.uc REPORT_VERSION, the
// Android form); older routers report only the name. A registered build
// takes the metrics of "<name>+<code>"; else the metrics of its name, which
// every registered build of that name shares (sharedBuilds).
export function routerVersionMetrics(
  version: { version: string, versionCode: number },
  metrics: Record<string, VersionMetrics>,
  versions: { version: string }[],
) {
  const exactKey = `${version.version}+${version.versionCode}`
  const exact = metrics[exactKey]
  if (exact) return { key: exactKey, metrics: exact, sharedBuilds: 1 }
  const byName = metrics[version.version] ?? null
  return {
    key: version.version,
    metrics: byName,
    sharedBuilds: byName ? versions.filter(v => v.version === version.version).length : 0,
  }
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = bytes
  let unit = 0
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024
    unit += 1
  }
  return `${n.toFixed(unit === 0 ? 0 : 2)} ${units[unit]}`
}

export function formatMetricNumber(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return Math.round(Number(value) || 0).toLocaleString('en-US')
}

export function formatMetricBytes(bytes?: number | null) {
  if (bytes === null || bytes === undefined) return '-'
  return formatBytes(Number(bytes || 0))
}

// Local time, MM-DD HH:mm.
export function formatMetricTime(epochMs?: number | null) {
  if (!epochMs) return '-'
  const date = new Date(epochMs)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function trafficMetricTitle(metrics?: VersionMetrics | null) {
  if (!metrics) return '-'
  return `${formatMetricBytes(metrics.trafficUpBytes)} up / ${formatMetricBytes(metrics.trafficDownBytes)} down`
}

// For Array.sort: directionMultiplier 1 ascending, -1 descending. Missing
// values go last in both directions.
export function compareMetricSortValues(a: string | number | null, b: string | number | null, directionMultiplier: number) {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  let result: number
  if (typeof a === 'string' || typeof b === 'string') {
    result = String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' })
  } else {
    result = a - b
  }
  return result * directionMultiplier
}
